import classnames from 'classnames';
import type { ReactNode } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import { Radio } from '#src/atoms/inputs/Radio/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';

const Root = styled.div<{
  withSpacing: boolean;
  row: boolean;
  flexWrap: boolean;
}>`
  ${({ withSpacing }) =>
    withSpacing &&
    css`
      display: flex;
      flex-direction: column;
      gap: var(--unit);
    `}
  ${({ row }) =>
    row &&
    css`
      display: flex;
      flex-direction: row;
      flex: 1;
      gap: var(--radio-group-gap, var(--double-unit));
      justify-content: space-between;
      & > * {
        flex: 1;
        min-width: var(--radio-width);
        text-align: center;
      }
    `}
  ${({ flexWrap }) =>
    flexWrap &&
    css`
      flex-wrap: wrap;
      row-gap: var(--unit);
    `}
`;

const RadioWrapper = styled.div<{
  checked: boolean;
}>`
  background: var(--c-radio-bg, var(--c-nd-50));
  color: var(--c-nd-400);
  /* Select inner radio to make the padding clickable */
  > * {
    padding: var(--radio-padding, var(--double-unit));
  }
  &:first-child {
    border-top-left-radius: var(--unit);
    border-top-right-radius: var(--unit);
  }
  &:last-child {
    border-bottom-left-radius: var(--unit);
    border-bottom-right-radius: var(--unit);
  }
  &:hover {
    &:not(.disabled) {
      background: var(--c-nd-100);
    }
  }
  &.checked {
    background: var(--c-nd-100);
    color: var(--c-nd-900);
  }
  &.ghost {
    color: var(--c-white);
    background-color: var(--c-black);
  }
  &.ghost.checked {
    color: var(--c-white);
    background-color: var(--c-blue-400);
  }

  &.border {
    outline: 1px solid var(--c-nd-200);
    outline-offset: -1px;
    border-radius: var(--unit);
  }

  &.rounded {
    border-radius: var(--quadruple-unit);
  }

  &.modal {
    background: var(--c-nd-50);
    outline: 1px solid var(--c-nd-200);
    outline-offset: -1px;
    &.border {
      background: transparent;
    }
    &.checked {
      background: rgba(70, 98, 247, 0.16);
      outline: 2px solid var(--c-blue-400);
      outline-offset: -2px;
    }
  }
  &.modalSelect {
    background: transparent;
    &.checked {
      background: var(--c-nd-100);
    }
    &:hover {
      background: var(--c-nd-100);
    }
    &.disabled {
      opacity: 0.5;
    }
  }
`;
const Extra = styled.div`
  padding: var(--unit);
`;

type Props<T> = {
  options: {
    label: string | ReactNode;
    value: T;
    helper?: ReactNode;
    disabled?: boolean;
    optional?: ReactNode;
    Content?: (props: { isSelected: boolean }) => ReactNode;
    extraContent?: ReactNode;
  }[];
  name: string;
  initiallySelectedValue?: T;
  rounded?: boolean;
  onChange: (value: T) => void;
  modal?: boolean;
  withSpacing?: boolean;
  modalSelect?: boolean;
  hideRadio?: boolean;
  border?: boolean;
  row?: boolean;
  ghost?: boolean;
  preventPreselection?: boolean;
  flexWrap?: boolean;
  allowUnselect?: boolean;
  clean?: () => void;
  center?: boolean;
  reverse?: boolean;
};

type ManagedProps<T> = Props<T> & {
  value?: T;
};

export const RadioGroup = <T extends string>({
  options,
  initiallySelectedValue,
  name,
  rounded = false,
  modal = false,
  withSpacing = false,
  border = false,
  onChange,
  modalSelect = false,
  hideRadio,
  row = false,
  center,
  ghost,
  preventPreselection,
  flexWrap = false,
  clean,
  reverse = false,
  ...rest
}: ManagedProps<T> | Props<T>) => {
  const isManagedMode = 'value' in rest;
  const [selectedValue, setSelectedValue] = useState(
    !preventPreselection
      ? initiallySelectedValue || options[0].value
      : undefined
  );

  const value = isManagedMode ? rest.value : selectedValue;

  return (
    <Root withSpacing={withSpacing} row={row} flexWrap={flexWrap}>
      {options.map(option => {
        const checked = value === option.value;
        return (
          <Vertical key={option.value} gap={0}>
            <RadioWrapper
              className={classnames({
                checked,
                modal,
                modalSelect,
                border,
                ghost,
                disabled: option?.disabled,
                rounded,
              })}
              checked={checked}
            >
              <Radio
                hideRadio={hideRadio}
                name={name}
                checked={checked}
                center={center}
                value={option.value}
                onChange={() => {
                  if (!isManagedMode) setSelectedValue(option.value);
                  onChange(option.value);
                }}
                onClick={() => {
                  if (rest.allowUnselect && option.value === value) {
                    if (!isManagedMode) setSelectedValue(undefined);
                    if (option.value === value) clean?.();
                  }
                }}
                labelContent={
                  typeof option.label === 'string' ? (
                    <BodyM as="p" bold>
                      {option.label}
                    </BodyM>
                  ) : (
                    option.label
                  )
                }
                disabled={option.disabled}
                reverse={reverse}
                checkedColor="var(--c-blue-400)"
              />
              {option.helper}
              {option.optional}
            </RadioWrapper>
            {!!option.extraContent && <Extra>{option.extraContent}</Extra>}
            {option.Content?.({ isSelected: checked })}
          </Vertical>
        );
      })}
    </Root>
  );
};
